import { useEffect } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { isPast } from 'date-fns/isPast';
import { isToday } from 'date-fns/isToday';
import { isTomorrow } from 'date-fns/isTomorrow';
import { isValid } from 'date-fns/isValid';

import IllustrationError from '@/components/IllustrationError';
import { Link } from '@/components/Link/Link';
import Loader from '@/components/Loader';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconCheck from '@/icons/Check.svg';
import IconNoConnection from '@/icons/NoConnection.svg';
import IconTools from '@/icons/Tools.svg';
import { I18nWording } from '@/services/i18n';
import { ErrorBoundary } from '@/services/sentry';
import { createBEMClasses } from '@/utils/classname';
import { formatDate } from '@/utils/date';
import { useHealth } from '@/utils/health';

import './Health.css';

const { block } = createBEMClasses('health');

export const Health = () => {
  const { data: health, isError } = useHealth();

  useEffect(() => {
    if (health?.status === 'ok') {
      window.localStorage.removeItem('health');

      setTimeout(() => {
        const redirectTo = decodeURIComponent(
          new URLSearchParams(window.location.search).get('redirect') ?? '/'
        );

        window.location.href = redirectTo;
      }, 3000);
    }
  }, [health?.status]);

  return (
    <main className={block()}>
      <section>
        <ErrorBoundary>
          {navigator.onLine ? (
            <>
              {health?.status === 'unknown' && !isError && <Unknown />}
              {health?.status === 'ok' && <OK />}
              {health?.status === 'maintenance' && (
                <Maintenance endOfMaintenanceETA={health.endOfMaintenanceETA} />
              )}
              {health?.status === 'unavailable' && <Unavailable message={health?.message} />}
              {(!health || health?.status === 'unknown') && isError && <Unavailable />}
            </>
          ) : (
            <Offline />
          )}
        </ErrorBoundary>
      </section>
    </main>
  );
};

const Unknown = () => {
  return (
    <Loader
      style={{
        // @ts-ignore CSS variable
        '--width': '4rem',
      }}
    />
  );
};

const OK = () => {
  return (
    <>
      <IconCheck className="text-success" />
      <Title size="300">
        <Trans id="health.ok">
          The application is back online. You will be redirected shortly.
        </Trans>
      </Title>
    </>
  );
};

const Maintenance = ({ endOfMaintenanceETA }: { endOfMaintenanceETA: string }) => {
  const etaDate = new Date(endOfMaintenanceETA);
  const etaDateShort = formatDate(etaDate, {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  const etaDateLong = formatDate(new Date(endOfMaintenanceETA), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  const isEtaPast = isPast(etaDate);
  const isEtaToday = isToday(etaDate);
  const isEtaTomorrow = isTomorrow(etaDate);

  return (
    <>
      <IconTools className="text-warning" />
      <Title size="300">
        <Trans id="health.maintenance">Scheduled maintenance in progress</Trans>
      </Title>
      {isValid(etaDate) && (
        <Title weight="medium">
          {isEtaPast ? (
            <Trans id="health.maintenance.eta.past">
              We will let you know as soon as the application is back online
            </Trans>
          ) : isEtaToday ? (
            <Trans id="health.maintenance.eta.today">
              Estimated time of completion: {etaDateShort}
            </Trans>
          ) : isEtaTomorrow ? (
            <Trans id="health.maintenance.eta.tomorrow">
              Estimated time of completion: Tomorrow at {etaDateShort}
            </Trans>
          ) : (
            <Trans id="health.maintenance.eta.future">
              Estimated time of completion: {etaDateLong}
            </Trans>
          )}
        </Title>
      )}
      <Paragraph>
        <Trans id="health.maintenance.automatic-reload">
          The application will automatically reload once the maintenance is complete
        </Trans>
      </Paragraph>
    </>
  );
};

const Unavailable = ({ message }: { message?: I18nWording }) => {
  const { i18n } = useLingui();

  const localizedMessage =
    message?.[i18n.locale as keyof I18nWording] ?? // Use user's locale
    message?.['en'] ?? // Fallback to English
    message?.[Object.keys(message ?? {})[0] as keyof I18nWording]; // Fallback to first available language

  return (
    <>
      <IllustrationError />
      <Title size="300">
        <Trans id="health.unavailable">
          The application is currently unavailable due to a technical issue
        </Trans>
      </Title>
      <Title weight="medium">
        <Trans id="health.unavailable.working-on-it">
          We are actively working to resolve the issue as quickly as possible
        </Trans>
      </Title>
      {localizedMessage && <Paragraph>&rdquo;{localizedMessage}&rdquo;</Paragraph>}
      <Paragraph>
        <Trans id="health.unavailable.automatic-reload">
          The application will automatically reload once the issue is resolved
        </Trans>
      </Paragraph>
    </>
  );
};

const Offline = () => {
  return (
    <>
      <IconNoConnection className="text-danger" />
      <Title size="300">
        <Trans id="health.offline">You are currently offline</Trans>
      </Title>
      <Title weight="medium">
        <Trans id="health.offline.description">
          Please check your internet connection then{' '}
          <Link to="/" reloadDocument>
            reload the application
          </Link>
        </Trans>
      </Title>
    </>
  );
};
